// ─────────────────────────────────────────────────────────────────────────────
// La tira de filtros de Reportería, en el ORDEN de la maqueta:
//   buscar persona · OU · [Site] · Fundamental · rango de fechas · limpiar
//
// 2026-09-24 — Site vuelve a la tira, pero SOLO en Diario, Brillamos y Demográfico (requerimiento
// «Visibilidad y lógica del filtro de Site»). Quien decide en qué pestaña se ve es
// `RecognitionAnalyticsView` (`TABS_CON_SITE`); esta tira solo lo pinta cuando recibe `sites`.
//
// 🔴 Por qué es propia y no la `FilterBar` compartida. `FilterBar` la usa también el módulo Analytics
// genérico (D-13) y su orden es otro (fechas → OU → Fundamental). Para llegar al orden de la maqueta
// había que reordenarla, y reordenar un componente compartido cambia una pantalla que nadie pidió
// tocar. Esta tira es de Reportería y solo de Reportería; `FilterBar` se queda intacta para Analytics.
//
// 🔴 BU / Site / Depto / Año / Mes se retiraron de esta tira (2026-09-02, pedido directo sobre la
// pantalla en producción). Año/Mes duplicaban el rango de fechas —que es lo que pide la Sección 7 de
// la Especificación: «el selector Año/Mes se reemplaza por un calendario de rango de fechas»— y BU/
// Site/Depto, sin catálogo maestro (RF-152), solo filtraban de verdad la pestaña Diario; en las otras
// seis el backend los ignoraba en silencio, justo el riesgo que ya advertía
// `docs/contexto/REPORTERIA-REGLAS.md` («un selector que no filtra nada es peor que ausente»).
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonPicker } from '@/components/common';
import { OuTreeSelect } from '@/components/common/OuTree';
import { CampoFecha } from './CampoFecha';
import { useOuScope } from '@/hooks/useOuScope';
import type { ReportFilters } from './useRecognitionTabFilters';

export interface TiraDeFiltrosProps {
  readonly filters: ReportFilters;
  readonly setFilter: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void;
  readonly resetFilters: () => void;
  /** Fundamentales del filtro, con su nombre ya resuelto. */
  readonly fundamentales: readonly { id: string; nombre: string }[];
  /**
   * Sites de las OUs elegidas (o del alcance). `null` = la pestaña activa no usa Site: el selector NO
   * se pinta. Un filtro que no filtra es peor que no tenerlo (`REPORTERIA-REGLAS.md`).
   */
  readonly sites?: readonly { id: string; nombre: string }[] | null;
  /** El valor de Site YA validado contra `sites` (ver `siteEfectivo` en RecognitionAnalyticsView). */
  readonly siteValue?: string;
  readonly sitesCargando?: boolean;
  /**
   * 2026-09-24 — controles que van al PRINCIPIO de la tira. Hoy solo el selector de campaña de
   * Brillamos Juntos (BJ-D3, REPORTERIA-REGLAS.md §8): la campaña se elige primero y manda sobre el
   * resto, así que se lee antes que ellos.
   */
  readonly inicio?: ReactNode;
  /**
   * Sesión 2026-10-02 (BJS-1) — controles que van JUSTO DESPUÉS de la OU, y con la OU al principio de
   * la tira: el estatus y la campaña de Brillamos Juntos. La lista de campañas depende de la OU, así
   * que la OU se elige primero (cambiarla después borraba la campaña y confundía).
   */
  readonly despuesDeOu?: ReactNode;
  /**
   * OUs que se MUESTRAN en el selector cuando difieren de `filters.ou` — el Admin de OU arranca con su
   * OU preseleccionada aunque la barra aún no la tenga guardada (BJ-F7).
   */
  readonly ouValue?: readonly string[];
  /** true = selector de OU bloqueado: Admin de OU con una sola OU (BJ-F7). */
  readonly ouBloqueada?: boolean;
  /**
   * 2026-09-28 (DEM-F2, REPORTERIA-REGLAS §10) — false = la pestaña activa no se filtra por persona
   * (Demográfico): el buscador NO se pinta, en vez de pintarlo con un aviso de «no aplica».
   */
  readonly personaVisible?: boolean;
}

export function TiraDeFiltros({
  filters, setFilter, resetFilters, fundamentales, sites = null, siteValue = '', sitesCargando = false,
  inicio, despuesDeOu, ouValue, ouBloqueada = false, personaVisible = true,
}: TiraDeFiltrosProps) {
  const { t } = useTranslation('recognition');
  // Las OUs del alcance salen del MISMO hook que usa `FilterBar`: si esta tira armara su propia
  // lista, dos pantallas podrían ofrecer alcances distintos al mismo usuario.
  // 'brillamos': el alcance de ESTE módulo, no el de Pulso — ver el doc-comment de `useOuScope`.
  const { scopedOuItems } = useOuScope('brillamos');

  /* OU — el mismo árbol jerárquico que el resto de la app, con selección múltiple (Fase 4 del plan
     de Reportería): cada OU elegida se expande a su subárbol en RecognitionAnalyticsView, no aquí —
     esta tira solo administra la SELECCIÓN. */
  const selectorDeOu = (
    <div className="rr-tira-ou">
      <OuTreeSelect
        mode="multi"
        variant="dropdown"
        items={scopedOuItems}
        value={[...(ouValue ?? filters.ou)]}
        onChange={(ids) => setFilter('ou', ids)}
        placeholder={t('reports.filters.allOus')}
        isDisabled={ouBloqueada}
      />
    </div>
  );

  return (
    <div className="rr-tira">
      {inicio}

      {/* BJS-1 — con controles «después de la OU» (Brillamos Juntos), la OU va primero. */}
      {despuesDeOu && selectorDeOu}
      {despuesDeOu}

      {/* Buscar persona. Combobox contra el directorio: lo que se elige es una PERSONA (su id
          viaja al backend), no un texto que se compare con los nombres ya pintados. */}
      {personaVisible && (
        <PersonPicker
          value={filters.persona}
          onChange={(persona) => setFilter('persona', persona)}
          placeholder={t('reports.filters.personPlaceholder')}
        />
      )}

      {!despuesDeOu && selectorDeOu}

      {/* 2-bis · Site. Solo en las pestañas que lo consumen; la lista sale del padrón de las OUs
             elegidas, así que cambia al cambiar la OU. */}
      {sites !== null && (
        <label className="rr-tira-campo">
          <span className="rr-sr-only">{t('reports.filters.site')}</span>
          <select
            className="rr-funnel-select"
            value={siteValue}
            onChange={(e) => setFilter('site', e.target.value)}
            disabled={sitesCargando || sites.length === 0}
            aria-label={t('reports.filters.site')}
          >
            <option value="">{t('reports.filters.allSites')}</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </label>
      )}

      {/* 3 · Fundamental. */}
      <label className="rr-tira-campo">
        <span className="rr-sr-only">{t('reports.filters.fundamental')}</span>
        <select
          className="rr-funnel-select"
          value={filters.fundFilter}
          onChange={(e) => setFilter('fundFilter', e.target.value)}
          disabled={fundamentales.length === 0}
        >
          <option value="">{t('reports.filters.allFundamentals')}</option>
          {fundamentales.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
        </select>
      </label>

      {/* 4 · Rango de fechas. Por omisión, la semana en curso (`getCurrentWeekRange`). */}
      {/* BJS-4 (sesión 2026-10-02) — siempre día/mes/año, sin depender del idioma del navegador. */}
      <div className="rr-tira-fechas">
        <CampoFecha
          value={filters.dateFrom}
          onChange={(iso) => setFilter('dateFrom', iso)}
          ariaLabel={t('reports.filters.dateFrom')}
          abrirCalendario={t('reports.filters.openCalendar')}
        />
        <span aria-hidden="true">–</span>
        <CampoFecha
          value={filters.dateTo}
          onChange={(iso) => setFilter('dateTo', iso)}
          ariaLabel={t('reports.filters.dateTo')}
          abrirCalendario={t('reports.filters.openCalendar')}
        />
      </div>

      <button type="button" className="rr-btn rr-btn--ghost rr-tira-reset" onClick={resetFilters}>
        <i className="fas fa-rotate-left" aria-hidden="true" /> {t('reports.filters.reset')}
      </button>
    </div>
  );
}
